<script setup lang="ts">
import type { VirtualAgentRagSettings } from '@/types/virtualAgentRagSettings'
import { useAnalytics } from '@/composables/analytics/useAnalytics'

const { t } = useI18n()
const { track } = useAnalytics()

const open = defineModel<boolean>('open', { default: false })
const modelValue = defineModel<VirtualAgentRagSettings>({ required: true })

const createDraft = (settings: VirtualAgentRagSettings): VirtualAgentRagSettings => ({
  ...settings,
})

const draft = ref<VirtualAgentRagSettings>(createDraft(modelValue.value))

const cancelChanges = () => {
  open.value = false
  return true
}

const confirmChanges = () => {
  track('RAG Settings Confirmed', {
    enhancedAccuracy: draft.value.km_enable_rerank ? 'Enabled' : 'Disabled',
    ragEmbedding: draft.value.km_embedded_rag ? 'Enabled' : 'Disabled',
    queryExpansion: draft.value.km_expand_query ? 'Enabled' : 'Disabled',
    scoreThreshold: draft.value.km_similarity_threshold,
    textSnippetsCount: draft.value.km_number_of_doc_chunks,
  })
  modelValue.value = createDraft(draft.value)
  open.value = false
  return true
}

watch(open, (isOpen) => {
  if (isOpen) {
    draft.value = createDraft(modelValue.value)
  }
})

watch(modelValue, (settings) => {
  if (!open.value) {
    draft.value = createDraft(settings)
  }
})
</script>

<template>
  <OModal
    v-model:open="open"
    :title="t('main_skills.advanced_rag_settings_modal_title')"
    :primary-action-title="t('confirm')"
    :secondary-action-title="t('cancel')"
    :primary-action-promise="confirmChanges"
    :secondary-action-promise="cancelChanges"
    primary-action-enabled
    secondary-action-enabled
    size="medium"
    content-class="!max-h-none text-neutral-1"
    scrollable-content-class="!overflow-visible !mt-0 !pr-0 !pl-0"
    closable
  >
    <template #subtitle>
      <span class="typography-body-2-long text-neutral-1">
        {{ t('main_skills.advanced_rag_settings_modal_description') }}
      </span>
    </template>

    <template #content>
      <div
        :class="[
          'pt-xl px-5 -mx-5 pb-2 -mb-2',
          'max-h-[320px]  overflow-y-auto',
          'flex flex-col space-y-6',
        ]"
      >
        <OSwitch
          v-model="draft.km_enable_rerank"
          :label="t('experimental.rag.km_enable_rerank')"
          :tooltip-content="t('experimental.rag.km_enable_rerank_tooltip')"
          dataTestid="rag-reranking-to-use"
        />

        <OSwitch
          v-model="draft.km_embedded_rag"
          :label="t('experimental.rag.km_embedded_rag')"
          :tooltip-content="t('experimental.rag.km_embedded_rag_tooltip')"
          dataTestid="rag-km-embedded-rag"
        />

        <OSwitch
          v-model="draft.km_expand_query"
          :label="t('experimental.rag.km_expand_query')"
          :tooltip-content="t('experimental.rag.km_expand_query_tooltip')"
          dataTestid="rag-km-expand-query"
        />

        <OSlider
          class="advanced-rag-slider mb-0 px-0!"
          v-model.number="draft.km_similarity_threshold"
          :label="t('experimental.rag.km_similarity_threshold')"
          :tooltip-content="t('experimental.rag.km_similarity_threshold_tooltip')"
          :min="0"
          :max="1"
          :step="0.01"
          :stepper="false"
          dataTestid="rag-score-threshold"
        />

        <OInput
          v-model.number="draft.km_number_of_doc_chunks"
          :label="t('experimental.rag.km_number_of_doc_chunks')"
          :tooltip-content="t('experimental.rag.km_number_of_doc_chunks_tooltip')"
          type="number"
          :min="5"
          :max="50"
          :step="1"
          size="M"
          :widthSize="2"
          dataTestid="rag-num-chunks"
        />
      </div>
    </template>
  </OModal>
</template>

<style scoped>
/* TODO: remove once decided by design team
  Source: https://diabolocom.clickup.com/t/36183802/AI-9103?comment=90120210067374
 */
.advanced-rag-slider :deep([data-testid='slider-control']) {
  padding-inline: calc((var(--iconsizes-xl) / 2) + var(--spacing-s));
}

.advanced-rag-slider :deep([data-testid='slider-title']),
.advanced-rag-slider :deep([data-testid='slider-value']) {
  font-family: var(--typography-body-1-strong-font-family);
  font-size: var(--text-sm);
  font-weight: var(--typography-body-2-strong-font-weight);
  line-height: var(--text-sm--line-height);
  color: var(--alias-neutral-10);
}
</style>
